import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Zap, ArrowLeft, Loader2, Star, CreditCard, ShieldCheck, ShieldAlert, AlertTriangle } from "lucide-react";
import UserMenu from "../components/UserMenu";

/**
 * /billing — user buys token packages. Calls Stripe Checkout via backend.
 */
export default function Billing() {
  const navigate = useNavigate();
  const [wallet, setWallet] = useState(null);
  const [packages, setPackages] = useState([]);
  const [ledger, setLedger] = useState([]);
  const [config, setConfig] = useState(null);
  const [busyPkg, setBusyPkg] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [w, p, l, c] = await Promise.all([
        api.get("/me/wallet"),
        api.get("/billing/packages"),
        api.get("/me/wallet/ledger?limit=10"),
        api.get("/billing/config").catch(() => ({ data: { mode: "test", ready: false } })),
      ]);
      setWallet(w.data);
      setPackages(p.data.items || []);
      setLedger(l.data.items || []);
      setConfig(c.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load billing");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const buy = async (pkg) => {
    if (!config?.ready) {
      toast.error("Payments are not configured yet. Please contact the administrator.");
      return;
    }
    setBusyPkg(pkg.id);
    try {
      const { data } = await api.post("/billing/checkout", {
        package_id: pkg.id,
        origin_url: window.location.origin,
      });
      if (!data?.url) throw new Error("No checkout URL");
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not start checkout");
      setBusyPkg(null);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border h-14 flex items-center justify-between px-4 lg:px-6 bg-background">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-muted-foreground hover:text-foreground" data-testid="billing-back">
            <ArrowLeft className="h-4 w-4" />
            <span className="text-sm font-mono">back</span>
          </button>
          <div className="h-4 w-px bg-border" />
          <div className="flex items-center gap-2">
            <Zap className="h-4 w-4 text-primary" />
            <span className="font-display font-bold tracking-tight">Billing</span>
          </div>
        </div>
        <UserMenu variant="workspace" />
      </header>

      <main className="max-w-6xl mx-auto px-6 lg:px-12 py-10">
        <div className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground font-mono">
          [ tokens · wallet · checkout ]
        </div>
        <h1 className="font-display font-black tracking-tight text-3xl lg:text-4xl mt-2" data-testid="billing-title">
          Top up your wallet
        </h1>
        <p className="text-muted-foreground mt-2 max-w-2xl">
          Tokens are spent across chat, file extraction, and voice. One-time purchase via Stripe — no subscription.
        </p>

        {/* Mode banner */}
        {config && (
          config.mode === "test" ? (
            <div className="mt-4 inline-flex items-center gap-1.5 text-[11px] font-mono px-2 py-1 rounded-sm border border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300" data-testid="mode-badge-test">
              <ShieldCheck className="h-3 w-3" /> TEST MODE · use card 4242 4242 4242 4242 · no real charge
            </div>
          ) : (
            <div className="mt-4 inline-flex items-center gap-1.5 text-[11px] font-mono px-2 py-1 rounded-sm border border-rose-500/40 bg-rose-500/10 text-rose-700 dark:text-rose-300" data-testid="mode-badge-live">
              <ShieldAlert className="h-3 w-3" /> LIVE PAYMENTS
            </div>
          )
        )}

        {/* Not configured warning */}
        {config && !config.ready && (
          <div className="mt-6 border border-amber-500/40 bg-amber-500/10 rounded-sm p-4 flex gap-3" data-testid="not-configured-banner">
            <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <div className="font-semibold text-amber-800 dark:text-amber-200">Payments are not yet configured</div>
              <div className="text-amber-700 dark:text-amber-300 text-xs mt-1">The administrator hasn't finished setting up Stripe. Token purchases are temporarily unavailable. You can still use any tokens already in your wallet.</div>
            </div>
          </div>
        )}

        {loading ? (
          <div className="mt-10 text-sm text-muted-foreground font-mono">Loading…</div>
        ) : (
          <>
            {/* Wallet hero */}
            <div className="mt-8 border border-border bg-card rounded-sm p-6 flex items-center justify-between flex-wrap gap-4" data-testid="wallet-hero">
              <div>
                <div className="text-[11px] uppercase tracking-wider font-mono text-muted-foreground">Current balance</div>
                <div className="flex items-baseline gap-2 mt-1">
                  <span className="font-display font-black text-5xl tabular-nums" data-testid="wallet-balance">
                    {Number(wallet?.balance_tokens || 0).toLocaleString()}
                  </span>
                  <span className="text-sm font-mono text-muted-foreground">tokens</span>
                </div>
                <div className="text-xs font-mono text-muted-foreground mt-1.5">
                  ≈ ${((wallet?.balance_tokens || 0) * (wallet?.token_unit_price_usd || 0.015)).toFixed(2)} USD
                </div>
              </div>
              <div className="grid grid-cols-3 gap-6">
                <Stat label="Purchased" value={wallet?.lifetime_purchased} />
                <Stat label="Bonus" value={wallet?.lifetime_bonus} />
                <Stat label="Spent" value={wallet?.lifetime_spent} />
              </div>
            </div>

            {/* Package grid */}
            <h2 className="font-display font-bold tracking-tight text-xl mt-12">Choose a package</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4" data-testid="package-grid">
              {packages.map((pkg) => (
                <PackageCard
                  key={pkg.id}
                  pkg={pkg}
                  busy={busyPkg === pkg.id}
                  onBuy={() => buy(pkg)}
                  disabled={!config?.ready}
                />
              ))}
              {packages.length === 0 && (
                <div className="col-span-full text-sm text-muted-foreground font-mono border border-border bg-muted/20 rounded-sm p-6">
                  No packages available right now.
                </div>
              )}
            </div>

            {/* Recent activity */}
            {ledger.length > 0 && (
              <>
                <h2 className="font-display font-bold tracking-tight text-xl mt-12">Recent activity</h2>
                <div className="mt-4 border border-border rounded-sm overflow-hidden" data-testid="ledger-recent">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/30 text-[11px] uppercase tracking-wider font-mono">
                      <tr>
                        <th className="text-left p-3 font-semibold">Date</th>
                        <th className="text-left p-3 font-semibold">Type</th>
                        <th className="text-left p-3 font-semibold">Reason</th>
                        <th className="text-right p-3 font-semibold">Tokens</th>
                        <th className="text-right p-3 font-semibold">Balance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ledger.map((row) => (
                        <tr key={row.id} className="border-t border-border">
                          <td className="p-3 font-mono text-xs text-muted-foreground whitespace-nowrap">
                            {new Date(row.created_at).toLocaleString()}
                          </td>
                          <td className="p-3"><TypeBadge t={row.type} /></td>
                          <td className="p-3 text-xs">{ledgerReason(row)}</td>
                          <td className={`p-3 text-right font-mono tabular-nums ${row.amount > 0 ? "text-emerald-600 dark:text-emerald-400" : "text-foreground"}`}>
                            {row.amount > 0 ? "+" : ""}{row.amount.toLocaleString()}
                          </td>
                          <td className="p-3 text-right font-mono tabular-nums text-muted-foreground">{row.balance_after.toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </>
        )}
      </main>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="text-right">
      <div className="text-[11px] uppercase tracking-wider font-mono text-muted-foreground">{label}</div>
      <div className="font-display font-bold text-lg tabular-nums mt-0.5">{Number(value || 0).toLocaleString()}</div>
    </div>
  );
}

function PackageCard({ pkg, busy, onBuy, disabled }) {
  const featured = !!pkg.badge;
  return (
    <div
      className={`relative border rounded-sm p-6 transition flex flex-col ${
        featured ? "border-primary bg-primary/5" : "border-border bg-card hover:border-foreground/30"
      } ${disabled ? "opacity-60" : ""}`}
      data-testid={`package-card-${pkg.id}`}
    >
      {pkg.badge && (
        <div className="absolute -top-2.5 right-4 inline-flex items-center gap-1 px-2 py-0.5 bg-primary text-primary-foreground text-[10px] font-bold uppercase tracking-wider rounded-sm">
          <Star className="h-3 w-3" /> {pkg.badge}
        </div>
      )}
      <div className="text-[11px] uppercase tracking-wider font-mono text-muted-foreground">{pkg.name}</div>
      <div className="mt-3 flex items-baseline gap-1.5">
        <span className="font-display font-black text-3xl tabular-nums">{Number(pkg.tokens).toLocaleString()}</span>
        <span className="text-sm font-mono text-muted-foreground">tokens</span>
      </div>
      <div className="mt-1 text-2xl font-display font-bold">${Number(pkg.price_usd).toFixed(2)}</div>
      <div className="text-xs font-mono text-muted-foreground mt-1">
        ${(pkg.price_usd / pkg.tokens * 1000).toFixed(2)} per 1k tokens
      </div>
      <Button
        onClick={onBuy}
        disabled={busy || disabled}
        className="mt-6 rounded-sm w-full"
        variant={featured ? "default" : "outline"}
        data-testid={`buy-${pkg.id}`}
        title={disabled ? "Payments not configured yet" : ""}
      >
        {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CreditCard className="h-4 w-4 mr-2" />}
        {busy ? "Redirecting…" : disabled ? "Unavailable" : "Buy with Stripe"}
      </Button>
    </div>
  );
}

function TypeBadge({ t }) {
  const cfg = {
    purchase: ["Purchase", "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300"],
    bonus: ["Bonus", "bg-violet-500/15 text-violet-700 dark:text-violet-300"],
    debit: ["Usage", "bg-muted text-muted-foreground"],
    refund: ["Refund", "bg-amber-500/15 text-amber-700 dark:text-amber-300"],
    admin_credit: ["Admin +", "bg-sky-500/15 text-sky-700 dark:text-sky-300"],
    admin_debit: ["Admin −", "bg-rose-500/15 text-rose-700 dark:text-rose-300"],
  };
  const [label, cls] = cfg[t] || [t, "bg-muted text-muted-foreground"];
  return <span className={`inline-block px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-wider rounded-sm ${cls}`}>{label}</span>;
}

function ledgerReason(row) {
  const meta = row.meta || {};
  if (row.type === "purchase") return `${meta.package_name || "Token bundle"}`;
  if (row.type === "bonus") return meta.reason || "Bonus";
  if (row.type === "debit") return meta.feature ? `${meta.feature.replace(/_/g, " ")}` : "Usage";
  if (row.type === "admin_credit" || row.type === "admin_debit") return meta.reason || "Admin adjustment";
  if (row.type === "refund") return meta.reason || "Refund";
  return "";
}
